/**
 * Contract API Service
 * Handles all contract-related API operations
 */

import { apiConfig, makeApiRequest } from './apiConfig';

/**
 * Analysis progress type for SSE updates
 */
export interface AnalysisProgress {
  stage: 'extracting' | 'sectioning' | 'analyzing' | 'synthesizing' | 'complete' | 'error';
  percentage: number;
  message: string;
  currentSection?: number;
  totalSections?: number;
  sectionTitle?: string;
}

/**
 * Email generation progress type for SSE updates (chunked email generation)
 */
export interface EmailGenerationProgress {
  stage: 'chunking' | 'analyzing_batch' | 'synthesizing' | 'complete' | 'error';
  currentBatch?: number;
  totalBatches?: number;
  percentage: number;
  message: string;
}

/**
 * Negotiation clause for email generation
 */
export interface NegotiationClauseForEmail {
  clauseNumber: string;
  clauseTitle: string;
  concern: string;
  suggestedRevision?: string;
  priority: 'deal-breaker' | 'high' | 'medium' | 'low';
  comments?: string[];
}

/**
 * Generation checkpoint for resumable email generation
 */
export interface GenerationCheckpoint {
  sessionId: string;
  completedBatches: number;
  totalBatches: number;
  batchResults: {
    batchIndex: number;
    content: string;
    success: boolean;
  }[];
  inputData: {
    tone: string;
    clauseCount: number;
    contractName: string;
    supplierName: string;
  };
  initiatedAt: string;
  lastUpdated: string;
  status: 'in_progress' | 'completed' | 'failed' | 'abandoned';
  initiatedBy?: string;
}

/**
 * Contract interface for API responses
 */
export type ContractCategory = 'beauty' | 'travel_retail' | 'toys' | 'food' | 'sports';
/** The AI's read of whether a copy carries the parties' signatures. */
export type SignatureStatus = 'signed' | 'partially_signed' | 'unsigned' | 'unclear';
export type ExpiryStatus = 'active' | 'expiring_soon' | 'expired';

/** Query options for the contract repository (signed contracts). */
export interface RepositoryContractFilters {
  search?: string;
  supplierId?: string;
  groupId?: string;
  category?: ContractCategory;
  expiryStatus?: ExpiryStatus;
  /** End date range, inclusive, YYYY-MM-DD */
  endFrom?: string;
  endTo?: string;
  sortBy?: 'endDate' | 'startDate' | 'contractTitle' | 'supplierName';
  sortOrder?: 'asc' | 'desc';
  /** Only contracts whose signed copy may not actually be signed. */
  signature?: 'flagged';
  page?: number;
  limit?: number;
}

export interface RepositoryContract {
  _id: string;
  contractTitle: string;
  contractNumber?: string;
  supplierId: string;
  groupId?: string;
  supplier?: { _id: string; companyName: string };
  group?: { _id: string; name: string; color?: string };
  category?: ContractCategory | null;
  startDate: string;
  endDate: string;
  status: string;
  reviewStatus: string;
  /** Worked out by the server from endDate on every read. */
  expiryStatus: ExpiryStatus;
  /** Whole days from today to endDate; negative once expired. */
  daysUntilExpiry: number;
  latestSignedFile: {
    _id: string;
    originalName: string;
    fileSize: number;
    mimeType: string;
    version: string;
    createdAt: string;
    /** Absent for documents added before the signature check existed. */
    signatureCheck?: { status: SignatureStatus; notes?: string; checkedAt: string };
  };
  signedFileCount: number;
}

export interface RepositoryContractPage {
  contracts: RepositoryContract[];
  /** Per-status totals for the same filters, ignoring expiryStatus. */
  counts: Record<ExpiryStatus, number>;
  /** Contracts (same filters) whose signed copy may not actually be signed. */
  flaggedSignatures: number;
  expiringSoonMonths: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/**
 * Read a JSON API response, throwing the most specific message the server
 * gave: the first field-level validation error if there is one.
 */
const readApiResult = async <T>(response: Response): Promise<T> => {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.success === false) {
    const retryAfter = Number(response.headers.get('Retry-After'));
    throw new ApiRequestError(
      body.errors?.[0]?.message ||
        body.message ||
        (response.status === 429 ? 'Too many requests. Try again shortly.' : `Request failed (${response.status})`),
      response.status,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined
    );
  }
  return body.data as T;
};

/** A failed API call, keeping the HTTP status so callers can tell a rate limit from a real error. */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Seconds the server asked us to wait, from Retry-After. */
    readonly retryAfterSeconds?: number
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** Details the AI suggested from a signed contract. Every field is a suggestion to confirm. */
export interface ExtractedContractDetails {
  supplierName: string | null;
  groupName: string | null;
  contractTitle: string | null;
  contractNumber: string | null;
  startDate: string | null;
  endDate: string | null;
  category: ContractCategory | null;
  notes: string | null;
  uncertainFields: Array<
    'supplierName' | 'groupName' | 'contractTitle' | 'contractNumber' | 'startDate' | 'endDate' | 'category'
  >;
  signatureStatus: SignatureStatus;
  signatureNotes: string | null;
}

export interface RepositoryExtraction {
  /** Null when the file couldn't be read; extractionError says why. */
  extracted: ExtractedContractDetails | null;
  extractionError: string | null;
  supplierMatch: { _id: string; companyName: string; defaultCategory: ContractCategory | null } | null;
  groupMatch: { _id: string; name: string } | null;
  /** An existing contract with the same contract number. */
  duplicate: { _id: string; contractTitle: string; contractNumber?: string } | null;
}

export interface NewRepositoryContract {
  supplierId: string;
  groupId?: string;
  contractTitle: string;
  contractNumber?: string;
  startDate: string;
  endDate: string;
  category?: ContractCategory | '';
  versionLabel?: string;
  notes?: string;
  /** Passed back from the upload-time read so it's recorded with the document. */
  signatureStatus?: SignatureStatus;
  signatureNotes?: string;
}

/**
 * Whether an active supplier has a valid signed contract:
 * none (nothing signed on file), expired (all ended), expiring (valid, but
 * ending soon), covered.
 */
export type SupplierCoverageState = 'none' | 'expired' | 'expiring' | 'covered';

export interface SupplierCoverageFilters {
  search?: string;
  category?: ContractCategory;
  /** missing = none + expired */
  coverage?: 'missing' | SupplierCoverageState | 'all';
}

export interface SupplierCoverage {
  _id: string;
  companyName: string;
  defaultCategory: ContractCategory | null;
  currencies: string[];
  coverage: SupplierCoverageState;
  activeContracts: number;
  expiringSoonContracts: number;
  expiredContracts: number;
  /** Contracts on file without a signed document yet. */
  unsignedContracts: number;
  latestEndDate: string | null;
  daysUntilLatestEnd: number | null;
  contractCategories: ContractCategory[];
}

export interface SupplierCoverageReport {
  suppliers: SupplierCoverage[];
  /** For the same search and category, whatever the coverage filter. */
  counts: Record<SupplierCoverageState, number> & { total: number };
  expiringSoonMonths: number;
}

export interface ContractApiResponse {
  _id?: string;
  id?: string | number;
  supplierId: string | number;
  supplier?: {
    _id?: string;
    companyName?: string;
    name?: string;
  };
  contractTitle: string;
  contractNumber?: string;
  year: number;
  startDate: string;
  endDate: string;
  status: 'draft' | 'active' | 'expired' | 'terminated';
  reviewStatus: 'not_received' | 'pending_review' | 'under_review' | 'finalized';
  description?: string;
  value?: number;
  paymentTerms?: string;
  renewalDate?: string;
  favorabilityRating?: number;
  attachments?: any[];
  negotiationPoints?: any[];
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  
  // New fields
  minPurchaseAmount?: number;
  marketingBudget?: number;
  renewalTermsCount?: number;
  incotermTerms?: string;
  paymentCurrency?: string;
  
  // Additional fields to match Contract interface
  currency?: string;
  lastModifiedBy?: string;
  reviewedBy?: string;
  approvedBy?: string;
  lastActivity?: string;
  activityCount?: number;
  tags?: string[];
  clauses?: any[];
  comments?: any[];
  // Negotiation email generated for the contract
  negotiationEmail?: {
    subject: string;
    body: string;
    generatedAt: string;
    generatedBy?: string;
  };
}

/**
 * Contract Service class
 */
class ContractService {
  /**
   * Get all contracts
   * @param {string} token - Auth token
   * @param {object} filters - Optional filters
   * @returns {Promise<ContractApiResponse[]>} List of contracts
   */
  async getAll(token: string, filters: Record<string, any> = {}): Promise<ContractApiResponse[]> {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const url = queryParams 
        ? `${apiConfig.endpoints.contracts.getAll}?${queryParams}`
        : apiConfig.endpoints.contracts.getAll;
      
      const response = await makeApiRequest<ContractApiResponse[]>(url, { method: 'GET' }, token);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching contracts:', error);
      throw error;
    }
  }

  /**
   * Get single contract by ID
   * @param {string} token - Auth token
   * @param {string} id - Contract ID
   * @returns {Promise<ContractApiResponse>} Contract data
   */
  async getById(token: string, id: string): Promise<ContractApiResponse> {
    try {
      const response = await makeApiRequest<ContractApiResponse>(
        apiConfig.endpoints.contracts.getOne(id),
        { method: 'GET' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error fetching contract ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new contract
   * @param {string} token - Auth token
   * @param {object} contractData - Contract data
   * @returns {Promise<ContractApiResponse>} Created contract
   */
  async create(token: string, contractData: any): Promise<ContractApiResponse> {
    try {
      const response = await makeApiRequest<ContractApiResponse>(
        apiConfig.endpoints.contracts.create,
        {
          method: 'POST',
          body: JSON.stringify(contractData)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error creating contract:', error);
      throw error;
    }
  }

  /**
   * Update existing contract
   * @param {string} token - Auth token
   * @param {string} id - Contract ID
   * @param {object} updates - Contract updates
   * @returns {Promise<ContractApiResponse>} Updated contract
   */
  async update(token: string, id: string, updates: any): Promise<ContractApiResponse> {
    try {
      const response = await makeApiRequest<ContractApiResponse>(
        apiConfig.endpoints.contracts.update(id),
        {
          method: 'PUT',
          body: JSON.stringify(updates)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error updating contract ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete contract
   * @param {string} token - Auth token
   * @param {string} id - Contract ID
   * @returns {Promise<boolean>} Success status
   */
  async delete(token: string, id: string): Promise<boolean> {
    try {
      await makeApiRequest(
        apiConfig.endpoints.contracts.delete(id),
        { method: 'DELETE' },
        token
      );
      return true;
    } catch (error) {
      console.error(`Error deleting contract ${id}:`, error);
      throw error;
    }
  }

  /**
   * Take a contract out of the repository: retires its signed documents and
   * keeps the contract, its drafts and its review.
   */
  async removeFromRepository(token: string, id: string): Promise<number> {
    const response = await makeApiRequest<{ removed: number }>(
      `${apiConfig.endpoints.contracts.getOne(id)}/signed-files`,
      { method: 'DELETE' },
      token
    );
    return response.data?.removed ?? 0;
  }

  /**
   * Upload file to contract
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {File} file - File to upload
   * @param {object} metadata - File metadata
   * @returns {Promise<any>} Upload result
   */
  async uploadFile(token: string, contractId: string, file: File, metadata: Record<string, any> = {}): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add metadata to form data
      Object.keys(metadata).forEach(key => {
        formData.append(key, metadata[key]);
      });
      
      const response = await fetch(
        `${apiConfig.baseURL}${apiConfig.endpoints.contracts.uploadFile(contractId)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Don't set Content-Type for FormData
          },
          body: formData
        }
      );
      
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const retryAfter = Number(response.headers.get('Retry-After'));
        throw new ApiRequestError(
          body.errors?.[0]?.message || body.message || `Upload failed: ${response.statusText}`,
          response.status,
          Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined
        );
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  /**
   * Get contract files
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @returns {Promise<any[]>} List of files
   */
  async getFiles(token: string, contractId: string): Promise<any[]> {
    try {
      const response = await makeApiRequest<any[]>(
        apiConfig.endpoints.contracts.getFiles(contractId),
        { method: 'GET' },
        token
      );
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching files for contract ${contractId}:`, error);
      throw error;
    }
  }

  /**
   * List signed contracts (the contract repository)
   * @param {string} token - Auth token
   * @param {RepositoryContractFilters} filters - Search, filter, sort and paging
   * @returns {Promise<RepositoryContractPage>} One page of contracts, status counts and pagination
   */
  async getRepositoryContracts(
    token: string,
    filters: RepositoryContractFilters = {}
  ): Promise<RepositoryContractPage> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });
    const query = params.toString();
    const url = query
      ? `${apiConfig.endpoints.contracts.repository}?${query}`
      : apiConfig.endpoints.contracts.repository;

    const response: any = await makeApiRequest<RepositoryContract[]>(url, { method: 'GET' }, token);
    return {
      contracts: response.data || [],
      counts: response.counts,
      flaggedSignatures: response.flaggedSignatures ?? 0,
      expiringSoonMonths: response.expiringSoonMonths,
      pagination: response.pagination
    };
  }

  /**
   * Which active suppliers have a valid signed contract
   */
  async getSupplierCoverage(token: string, filters: SupplierCoverageFilters = {}): Promise<SupplierCoverageReport> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, String(value));
    });
    const query = params.toString();
    const url = query
      ? `${apiConfig.endpoints.contracts.repositoryCoverage}?${query}`
      : apiConfig.endpoints.contracts.repositoryCoverage;
    const response: any = await makeApiRequest<SupplierCoverage[]>(url, { method: 'GET' }, token);
    return {
      suppliers: response.data || [],
      counts: response.counts,
      expiringSoonMonths: response.expiringSoonMonths
    };
  }

  /**
   * Read a signed contract and get suggested details for the repository form.
   * Nothing is stored.
   */
  async extractRepositoryContract(token: string, file: File): Promise<RepositoryExtraction> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${apiConfig.baseURL}${apiConfig.endpoints.contracts.repositoryExtract}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });
    return readApiResult<RepositoryExtraction>(response);
  }

  /**
   * Add a signed contract to the repository: creates the contract and stores
   * its signed document together.
   */
  async createRepositoryContract(
    token: string,
    file: File,
    details: NewRepositoryContract
  ): Promise<ContractApiResponse> {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(details).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') formData.append(key, String(value));
    });
    const response = await fetch(`${apiConfig.baseURL}${apiConfig.endpoints.contracts.repository}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });
    return readApiResult<ContractApiResponse>(response);
  }

  /**
   * Delete contract file
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {string} fileId - File ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteFile(token: string, contractId: string, fileId: string): Promise<boolean> {
    try {
      await makeApiRequest(
        apiConfig.endpoints.contracts.deleteFile(contractId, fileId),
        { method: 'DELETE' },
        token
      );
      return true;
    } catch (error) {
      console.error(`Error deleting file ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Add note to contract
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {object} noteData - Note data
   * @returns {Promise<any>} Created note
   */
  async addNote(token: string, contractId: string, noteData: any): Promise<any> {
    try {
      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.addNote(contractId),
        {
          method: 'POST',
          body: JSON.stringify(noteData)
        },
        token
      );
      return response.data;
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  }

  /**
   * Get contract notes
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @returns {Promise<any[]>} List of notes
   */
  async getNotes(token: string, contractId: string): Promise<any[]> {
    try {
      const response = await makeApiRequest<any[]>(
        apiConfig.endpoints.contracts.getNotes(contractId),
        { method: 'GET' },
        token
      );
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching notes for contract ${contractId}:`, error);
      throw error;
    }
  }

  /**
   * Edit contract note
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {string} noteId - Note ID
   * @param {object} noteData - Updated note data
   * @returns {Promise<any>} Updated note
   */
  async editNote(token: string, contractId: string, noteId: string, noteData: any): Promise<any> {
    try {
      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.editNote(contractId, noteId),
        {
          method: 'PUT',
          body: JSON.stringify(noteData)
        },
        token
      );
      return response.data;
    } catch (error) {
      console.error('Error editing note:', error);
      throw error;
    }
  }

  /**
   * Delete contract note
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {string} noteId - Note ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteNote(token: string, contractId: string, noteId: string): Promise<boolean> {
    try {
      await makeApiRequest(
        apiConfig.endpoints.contracts.deleteNote(contractId, noteId),
        { method: 'DELETE' },
        token
      );
      return true;
    } catch (error) {
      console.error(`Error deleting note ${noteId}:`, error);
      throw error;
    }
  }

  /**
   * Analyze contract
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {object} analysisData - Analysis parameters
   * @returns {Promise<any>} Analysis result
   */
  async analyzeContract(token: string, contractId: string, analysisData: any): Promise<any> {
    try {
      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.analyze(contractId),
        {
          method: 'POST',
          body: JSON.stringify(analysisData)
        },
        token
      );
      return response.data;
    } catch (error) {
      console.error('Error analyzing contract:', error);
      throw error;
    }
  }

  /**
   * Get contract analysis (latest version)
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @returns {Promise<any>} Analysis data (includes _id for analysisId)
   */
  async getAnalysis(token: string, contractId: string): Promise<any> {
    try {
      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.getAnalysis(contractId),
        { method: 'GET' },
        token
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching analysis for contract ${contractId}:`, error);
      throw error;
    }
  }

  /**
   * Get all analysis versions for a contract
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @returns {Promise<any[]>} List of analysis versions
   */
  async getAnalysisVersions(token: string, contractId: string): Promise<any[]> {
    try {
      const response = await makeApiRequest<any[]>(
        apiConfig.endpoints.contracts.getAnalysisVersions(contractId),
        { method: 'GET' },
        token
      );
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching analysis versions for contract ${contractId}:`, error);
      return [];
    }
  }

  /**
   * Get specific analysis version
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {number} version - Version number
   * @returns {Promise<any>} Analysis data for specific version
   */
  async getAnalysisVersion(token: string, contractId: string, version: number): Promise<any> {
    try {
      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.getAnalysisVersion(contractId, version),
        { method: 'GET' },
        token
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching analysis version ${version} for contract ${contractId}:`, error);
      throw error;
    }
  }

  /**
   * Get clause decisions for an analysis
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<any[]>} List of clause decisions
   */
  async getClauseDecisions(token: string, contractId: string, analysisId: string): Promise<any[]> {
    try {
      const response = await makeApiRequest<any[]>(
        apiConfig.endpoints.contracts.getClauseDecisions(contractId, analysisId),
        { method: 'GET' },
        token
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching clause decisions:', error);
      return [];
    }
  }

  /**
   * Update a clause decision
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {string} analysisId - Analysis ID
   * @param {string} clauseRef - Clause reference
   * @param {string} status - Decision status
   * @param {string} notes - Optional notes
   * @returns {Promise<any>} Updated decision
   */
  async updateClauseDecision(
    token: string,
    contractId: string,
    analysisId: string,
    clauseRef: string,
    status: string,
    notes?: string
  ): Promise<any> {
    try {
      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.updateClauseDecision(contractId, analysisId, clauseRef),
        {
          method: 'PUT',
          body: JSON.stringify({ status, notes })
        },
        token
      );
      return response.data;
    } catch (error) {
      console.error('Error updating clause decision:', error);
      throw error;
    }
  }

  /**
   * Add comment to a clause
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {string} analysisId - Analysis ID
   * @param {string} clauseRef - Clause reference
   * @param {string} text - Comment text
   * @returns {Promise<any>} Updated decision with comment
   */
  async addClauseComment(
    token: string,
    contractId: string,
    analysisId: string,
    clauseRef: string,
    text: string
  ): Promise<any> {
    try {
      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.addClauseComment(contractId, analysisId, clauseRef),
        {
          method: 'POST',
          body: JSON.stringify({ text })
        },
        token
      );
      return response.data;
    } catch (error) {
      console.error('Error adding clause comment:', error);
      throw error;
    }
  }

  /**
   * Update workflow state for a contract
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {object} workflowState - Workflow state data
   * @returns {Promise<any>} Updated contract
   */
  async updateWorkflowState(
    token: string,
    contractId: string,
    workflowState: {
      currentStep: number;
      completedSteps: number[];
      selectedForNegotiation: string[];
    }
  ): Promise<any> {
    try {
      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.updateWorkflowState(contractId),
        {
          method: 'PUT',
          body: JSON.stringify(workflowState)
        },
        token
      );
      return response.data;
    } catch (error) {
      console.error('Error updating workflow state:', error);
      throw error;
    }
  }

  /**
   * Generate negotiation email
   * @param {string} token - Auth token
   * @param {object} emailData - Email generation parameters
   * @returns {Promise<any>} Generated email
   */
  async generateNegotiationEmail(token: string, emailData: any): Promise<any> {
    try {
      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.generateNegotiationEmail,
        {
          method: 'POST',
          body: JSON.stringify(emailData)
        },
        token
      );
      return response.data;
    } catch (error) {
      console.error('Error generating negotiation email:', error);
      throw error;
    }
  }

  /**
   * Analysis progress callback type
   */
  onAnalysisProgress?: (progress: AnalysisProgress) => void;

  /**
   * Run AI analysis on a contract's uploaded file (with optional SSE progress)
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {function} onProgress - Optional progress callback for SSE updates
   * @param {boolean} forceRefresh - Force a new analysis even if cached
   * @param {string} model - Optional model ID to use for analysis
   * @param {string} provider - Optional AI provider ('anthropic' or 'openai')
   * @returns {Promise<any>} Analysis result
   */
  async runAIAnalysis(
    token: string,
    contractId: string,
    onProgress?: (progress: AnalysisProgress) => void,
    forceRefresh: boolean = false,
    model?: string,
    provider?: 'anthropic' | 'openai'
  ): Promise<any> {
    try {
      // First, get the uploaded files for this contract
      const files = await this.getFiles(token, contractId);

      if (!files || files.length === 0) {
        throw new Error('No files uploaded for this contract. Please upload a contract document first.');
      }

      // Get the most recent file (or the first one)
      const latestFile = files[files.length - 1];

      // Download the file to get its content
      const fileUrl = `${apiConfig.baseURL}${apiConfig.endpoints.contracts.downloadFile(latestFile._id || latestFile.id)}`;
      const fileResponse = await fetch(fileUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!fileResponse.ok) {
        throw new Error('Failed to download contract file');
      }

      const fileBlob = await fileResponse.blob();
      const file = new File([fileBlob], latestFile.originalName || latestFile.filename, {
        type: latestFile.mimeType || 'application/pdf'
      });

      // Create FormData to send to AI analysis endpoint
      const formData = new FormData();
      formData.append('document', file);
      formData.append('contractId', contractId);
      if (forceRefresh) {
        formData.append('forceRefresh', 'true');
      }
      if (model) {
        formData.append('model', model);
      }
      if (provider) {
        formData.append('provider', provider);
      }

      // Use SSE-enabled enhanced endpoint if progress callback provided
      if (onProgress) {
        return this.runEnhancedAnalysisWithSSE(token, formData, contractId, latestFile.originalName || latestFile.filename, onProgress);
      }

      // Send to AI analysis endpoint (legacy)
      const analysisResponse = await fetch(`${apiConfig.baseURL}${apiConfig.endpoints.ai.analyze}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!analysisResponse.ok) {
        const errorData = await analysisResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'AI analysis failed');
      }

      const analysisResult = await analysisResponse.json();

      if (!analysisResult.success) {
        throw new Error(analysisResult.message || 'AI analysis failed');
      }

      // Save the analysis to the contract
      await this.saveAnalysis(token, contractId, analysisResult.data, latestFile.originalName || latestFile.filename);

      return analysisResult.data;
    } catch (error) {
      console.error('Error running AI analysis:', error);
      throw error;
    }
  }

  /**
   * Run enhanced AI analysis with Server-Sent Events for progress updates
   * Uses Solution 1: Multi-Pass Chunked Analysis
   * Uses fetch with ReadableStream for reliable streaming
   */
  private async runEnhancedAnalysisWithSSE(
    token: string,
    formData: FormData,
    contractId: string,
    fileName: string,
    onProgress: (progress: AnalysisProgress) => void
  ): Promise<any> {
    const url = `${apiConfig.baseURL}/api/ai/analyze-enhanced`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream'
      },
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const error = JSON.parse(errorText);
        throw new Error(error.message || 'Analysis failed');
      } catch {
        throw new Error(`Analysis failed with status ${response.status}`);
      }
    }

    // Read the stream
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let analysisResult: any = null;

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events from buffer
      const lines = buffer.split('\n');
      // Keep the last incomplete line in the buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.substring(6));

            if (data.type === 'progress') {
              onProgress({
                stage: data.stage,
                percentage: data.percentage,
                message: data.message,
                currentSection: data.currentSection,
                totalSections: data.totalSections,
                sectionTitle: data.sectionTitle
              });
            } else if (data.type === 'complete') {
              analysisResult = data.data;
              onProgress({
                stage: 'complete',
                percentage: 100,
                message: 'Analysis complete!'
              });
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (e) {
            // Only throw if it's a real error, not a parse error
            if (e instanceof Error && e.message !== 'Unexpected end of JSON input') {
              throw e;
            }
          }
        }
      }
    }

    if (!analysisResult) {
      throw new Error('Analysis did not return a result');
    }

    // Save the analysis to the contract
    await this.saveAnalysis(token, contractId, analysisResult, fileName);
    return analysisResult;
  }

  /**
   * Save analysis data to contract
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {object} analysisData - Analysis data to save
   * @param {string} fileName - File name
   * @returns {Promise<any>} Save result
   */
  async saveAnalysis(token: string, contractId: string, analysisData: any, fileName: string): Promise<any> {
    try {
      // Transform the AI analysis result to match the expected format
      const transformedAnalysis = this.transformAnalysisForStorage(analysisData);

      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.analyze(contractId),
        {
          method: 'POST',
          body: JSON.stringify({
            analysisData: transformedAnalysis,
            fileName
          })
        },
        token
      );
      return response.data;
    } catch (error) {
      console.error('Error saving analysis:', error);
      throw error;
    }
  }

  /**
   * Transform AI analysis result to storage format
   * @param {object} aiAnalysis - Raw AI analysis from backend
   * @returns {object} Transformed analysis
   */
  private transformAnalysisForStorage(aiAnalysis: any): any {
    // Check if the data is already in the correct format (from enhanced analysis)
    // The enhanced analysis already has properly formatted clauses with reference, title, etc.
    if (aiAnalysis.jsonAnalysis?.clauses?.[0]?.reference) {
      // Data is already in correct format from enhanced analysis
      return {
        executive_summary: aiAnalysis.executive_summary || aiAnalysis.jsonAnalysis?.executive_summary,
        clauses: aiAnalysis.jsonAnalysis?.clauses || aiAnalysis.clauses,
        negotiation_points: aiAnalysis.negotiationPoints || [],
        risk_assessment: aiAnalysis.riskAssessment || {},
        jsonAnalysis: aiAnalysis.jsonAnalysis || aiAnalysis
      };
    }

    // Check if clauses already have the correct structure (reference field exists)
    if (aiAnalysis.clauses?.[0]?.reference) {
      return {
        executive_summary: aiAnalysis.executive_summary || {
          overview: aiAnalysis.summary || '',
          overall_favorability_score: aiAnalysis.riskAssessment?.favorabilityScore || 5,
          critical_issues: aiAnalysis.legalConcerns || [],
          key_recommendations: aiAnalysis.riskAssessment?.recommendations || []
        },
        clauses: aiAnalysis.clauses,
        negotiation_points: aiAnalysis.negotiationPoints || [],
        risk_assessment: aiAnalysis.riskAssessment || {},
        jsonAnalysis: aiAnalysis
      };
    }

    // Legacy format transformation (for old analysis data)
    return {
      executive_summary: {
        overview: aiAnalysis.summary || '',
        overall_favorability_score: aiAnalysis.riskAssessment?.overallRisk === 'low' ? 8
          : aiAnalysis.riskAssessment?.overallRisk === 'medium' ? 5 : 3,
        critical_issues: (aiAnalysis.legalConcerns || [])
          .filter((c: any) => c.severity === 'critical' || c.severity === 'high')
          .map((c: any) => ({
            title: c.concern,
            description: c.description
          })),
        key_recommendations: aiAnalysis.riskAssessment?.recommendations || []
      },
      clauses: (aiAnalysis.clauses || []).map((clause: any, index: number) => ({
        reference: clause.id || clause.clauseRef || `clause-${index + 1}`,
        title: clause.section || clause.title || `Clause ${index + 1}`,
        full_heading: `${clause.id || index + 1} - ${clause.section || clause.title || 'Contract Clause'}`,
        summary: clause.analysis || clause.clause || '',
        party_obligations: clause.clause || '',
        problematic_subclauses: clause.concerns && clause.concerns.length > 0
          ? clause.concerns.map((concern: string, idx: number) => ({
              subref: `${clause.id || index + 1}.${idx + 1}`,
              issue: concern,
              risk_level: clause.riskLevel === 'high' ? 'High' : clause.riskLevel === 'medium' ? 'Medium' : 'Low',
              icon: clause.riskLevel === 'high' ? '⚠️' : '⚡',
              note: clause.recommendation || ''
            }))
          : clause.riskLevel === 'high' || clause.riskLevel === 'medium' ? [{
              subref: clause.id || `${index + 1}`,
              issue: clause.clause || 'Review required',
              risk_level: clause.riskLevel === 'high' ? 'High' : 'Medium',
              icon: clause.riskLevel === 'high' ? '⚠️' : '⚡',
              note: clause.recommendation || ''
            }] : [],
        negotiation_analysis: {
          favorability: clause.isFavorable ? 'balanced' : 'favors counterparty',
          market_standard: 'Review required',
          recommended_modification: clause.recommendation || ''
        },
        risk_assessment: {
          legal_risk: clause.riskLevel === 'high' ? 'High' : clause.riskLevel === 'medium' ? 'Medium' : 'Low',
          financial_exposure: 'Review required',
          operational_impact: 'Review required',
          regulatory_concerns: 'Standard'
        },
        actionable_recommendations: {
          immediate_actions: clause.recommendation || '',
          approvals_needed: clause.riskLevel === 'high' ? 'Legal approval' : '',
          proposed_redline: ''
        },
        icons: {
          risk_icon: clause.riskLevel === 'high' ? '🔴' : clause.riskLevel === 'medium' ? '🟡' : '🟢',
          favorability_icon: clause.isFavorable ? '👍' : '👎',
          action_icon: '📋'
        }
      })),
      negotiation_points: aiAnalysis.negotiationPoints || [],
      risk_assessment: aiAnalysis.riskAssessment || {},
      jsonAnalysis: aiAnalysis
    };
  }

  /**
   * Complete review and save negotiation email
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {object} data - Review completion data
   * @returns {Promise<any>} Updated contract
   */
  async completeReview(
    token: string,
    contractId: string,
    data: {
      negotiationEmail?: {
        subject: string;
        body: string;
      };
      reviewStatus?: string;
    }
  ): Promise<any> {
    try {
      const response = await makeApiRequest(
        apiConfig.endpoints.contracts.update(contractId),
        {
          method: 'PUT',
          body: JSON.stringify({
            reviewStatus: data.reviewStatus || 'finalized',
            ...(data.negotiationEmail && {
              negotiationEmail: {
                subject: data.negotiationEmail.subject,
                body: data.negotiationEmail.body,
                generatedAt: new Date().toISOString()
              }
            })
          })
        },
        token
      );
      return response.data;
    } catch (error) {
      console.error('Error completing review:', error);
      throw error;
    }
  }

  /**
   * Generate negotiation email with streaming progress (for large clause counts)
   * @param {string} token - Auth token
   * @param {object} emailData - Email generation parameters
   * @param {function} onProgress - Progress callback for SSE updates
   * @returns {Promise<string>} Generated email content
   */
  async generateNegotiationEmailWithProgress(
    token: string,
    emailData: {
      contractName: string;
      supplierName: string;
      clauses: NegotiationClauseForEmail[];
      tone?: string;
    },
    onProgress: (progress: EmailGenerationProgress) => void
  ): Promise<string> {
    const response = await fetch(
      `${apiConfig.baseURL}/api/contracts/generate-negotiation-email-streaming`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify(emailData)
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const error = JSON.parse(errorText);
        throw new Error(error.message || `Generation failed: ${response.status}`);
      } catch {
        throw new Error(`Generation failed with status ${response.status}`);
      }
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let result = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.substring(6));
            if (data.type === 'progress') {
              onProgress({
                stage: data.stage,
                percentage: data.percentage,
                message: data.message,
                currentBatch: data.currentBatch,
                totalBatches: data.totalBatches
              });
            } else if (data.type === 'complete') {
              result = data.email;
              onProgress({
                stage: 'complete',
                percentage: 100,
                message: 'Email generation complete!'
              });
            } else if (data.type === 'error') {
              throw new Error(data.message || 'Email generation failed');
            }
          } catch (e) {
            // Only throw if it's a real error, not a JSON parse error
            if (e instanceof Error && !e.message.includes('JSON') && !e.message.includes('Unexpected')) {
              throw e;
            }
          }
        }
      }
    }

    if (!result) {
      throw new Error('Email generation did not return a result');
    }

    return result;
  }

  /**
   * Get generation checkpoint for a contract (if any)
   * Used to check for resumable email generation
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @returns {Promise<GenerationCheckpoint | null>} Checkpoint data or null
   */
  async getGenerationCheckpoint(token: string, contractId: string): Promise<GenerationCheckpoint | null> {
    try {
      const response = await makeApiRequest<{ checkpoint: GenerationCheckpoint | null }>(
        apiConfig.endpoints.contracts.getGenerationCheckpoint(contractId),
        { method: 'GET' },
        token
      );
      return response.data?.checkpoint || null;
    } catch (error) {
      console.error('Error fetching generation checkpoint:', error);
      return null;
    }
  }

  /**
   * Resume email generation from checkpoint
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @param {function} onProgress - Progress callback
   * @returns {Promise<string>} Completed email content
   */
  async resumeEmailGeneration(
    token: string,
    contractId: string,
    onProgress: (progress: EmailGenerationProgress) => void
  ): Promise<string> {
    const response = await fetch(
      `${apiConfig.baseURL}${apiConfig.endpoints.contracts.resumeEmailGeneration(contractId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream'
        }
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const error = JSON.parse(errorText);
        throw new Error(error.message || `Resume failed: ${response.status}`);
      } catch {
        throw new Error(`Resume failed with status ${response.status}`);
      }
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let result = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.substring(6));
            if (data.type === 'progress') {
              onProgress({
                stage: data.stage,
                percentage: data.percentage,
                message: data.message,
                currentBatch: data.currentBatch,
                totalBatches: data.totalBatches
              });
            } else if (data.type === 'complete') {
              result = data.email;
              onProgress({
                stage: 'complete',
                percentage: 100,
                message: 'Email generation resumed and complete!'
              });
            } else if (data.type === 'error') {
              throw new Error(data.message || 'Resume failed');
            }
          } catch (e) {
            if (e instanceof Error && !e.message.includes('JSON') && !e.message.includes('Unexpected')) {
              throw e;
            }
          }
        }
      }
    }

    if (!result) {
      throw new Error('Resume did not return a result');
    }

    return result;
  }

  /**
   * Clear generation checkpoint for a contract
   * @param {string} token - Auth token
   * @param {string} contractId - Contract ID
   * @returns {Promise<boolean>} Success status
   */
  async clearGenerationCheckpoint(token: string, contractId: string): Promise<boolean> {
    try {
      await makeApiRequest(
        apiConfig.endpoints.contracts.clearGenerationCheckpoint(contractId),
        { method: 'DELETE' },
        token
      );
      return true;
    } catch (error) {
      console.error('Error clearing generation checkpoint:', error);
      return false;
    }
  }

  /**
   * Bulk delete contracts
   * @param {string} token - Auth token
   * @param {string[]} contractIds - Array of contract IDs
   * @returns {Promise<boolean>} Success status
   */
  async bulkDelete(token: string, contractIds: string[]): Promise<boolean> {
    try {
      // Delete contracts one by one
      // Could be optimized with a bulk delete endpoint if available
      const deletePromises = contractIds.map(id => this.delete(token, id));
      await Promise.all(deletePromises);
      return true;
    } catch (error) {
      console.error('Error in bulk delete:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const contractService = new ContractService();

export default contractService;
